/**
 * Registro central de notificações do ChatContar.
 *
 * Objetivo: sincronizar a leitura entre o app (Electron) e a web.
 * - Guarda as notificações abertas por conversa para poder fechá-las
 *   assim que a conversa for lida em QUALQUER dispositivo.
 * - Guarda o último `last_read_at` conhecido do usuário por conversa,
 *   para suprimir notificações de mensagens já lidas em outro lugar.
 */

import { tituloDaMencao, type TipoDeMencao } from '@/lib/mencoesChat';

/**
 * Título e corpo da notificação de mensagem nova.
 *
 * Sem "ChatContar" no título: o Windows já escreve o nome do app no alto da
 * notificação, e o prefixo só empurrava o que importa para fora da linha. Em
 * grupo, o título é o grupo e o corpo começa por quem escreveu — antes o grupo
 * aparecia sem o remetente, e não dava para saber quem tinha mandado. Em
 * conversa direta a pessoa já é o título. Menção troca o título ("Ana
 * mencionou você · Fiscal"), que então já diz quem foi.
 */
export function montarNotificacao(p: {
  remetente: string;
  /** Nome do grupo; ausente em conversa direta. */
  grupo?: string | null;
  texto: string;
  mencao?: TipoDeMencao | null;
}): { titulo: string; corpo: string } {
  if (p.mencao) return { titulo: tituloDaMencao(p.remetente, p.mencao, p.grupo), corpo: p.texto };
  if (p.grupo) return { titulo: p.grupo, corpo: `${p.remetente}: ${p.texto}` };
  return { titulo: p.remetente, corpo: p.texto };
}

const openNotifications = new Map<string, Notification[]>();
const lastReadByConv = new Map<string, number>();

/**
 * O `last_read_at` a gravar quando o usuário lê a conversa.
 *
 * O banco conta como não lida toda mensagem com `created_at > last_read_at`, e
 * `created_at` sai do relógio do servidor. Com o relógio da máquina atrasado,
 * `new Date()` cai antes da mensagem que acabou de chegar, e ela segue contando
 * como não lida com a conversa aberta na tela. Nunca marcar antes da última
 * mensagem vista resolve sem depender da hora do servidor. O milissegundo a
 * mais cobre os microssegundos que o `Date` do JavaScript descarta.
 */
export function instanteDeLeitura(ultimaVista?: string | null, agora: Date = new Date()): string {
  const vista = ultimaVista ? new Date(ultimaVista).getTime() : NaN;
  if (Number.isNaN(vista)) return agora.toISOString();
  return new Date(Math.max(agora.getTime(), vista + 1)).toISOString();
}

/** Atualiza o marcador de leitura (vindo do banco / realtime / leitura local). */
export function setConversationRead(conversationId: string, lastReadAt?: string | null) {
  const ts = lastReadAt ? new Date(lastReadAt).getTime() : Date.now();
  const prev = lastReadByConv.get(conversationId) ?? 0;
  if (ts >= prev) lastReadByConv.set(conversationId, ts);
  // Leu em algum dispositivo → fecha o que estiver aberto aqui
  dismissChatNotifications(conversationId);
}

/** Aplica em lote os participantes do usuário atual. */
export function syncReadState(
  entries: Array<{ conversation_id: string; last_read_at: string | null }>,
) {
  entries.forEach((p) => {
    if (!p.last_read_at) return;
    const ts = new Date(p.last_read_at).getTime();
    const prev = lastReadByConv.get(p.conversation_id) ?? 0;
    if (ts > prev) {
      lastReadByConv.set(p.conversation_id, ts);
      dismissChatNotifications(p.conversation_id);
    }
  });
}

/** A mensagem ainda merece alerta (som/notificação)? */
export function shouldAlert(conversationId: string, messageCreatedAt: string): boolean {
  const readTs = lastReadByConv.get(conversationId);
  if (!readTs) return true;
  // 1s de tolerância para diferença de relógio entre cliente e servidor
  return new Date(messageCreatedAt).getTime() > readTs - 1000;
}

/** Fecha todas as notificações abertas de uma conversa. */
export function dismissChatNotifications(conversationId: string) {
  const list = openNotifications.get(conversationId);
  if (!list) return;
  list.forEach((n) => {
    try {
      n.close();
    } catch {
      /* noop */
    }
  });
  openNotifications.delete(conversationId);
}

/** Fecha todas as notificações abertas (ex.: logout / janela focada). */
export function dismissAllChatNotifications() {
  Array.from(openNotifications.keys()).forEach(dismissChatNotifications);
}

/** Exibe uma notificação rastreada, respeitando a leitura em outros dispositivos. */
export function showChatNotification(
  conversationId: string,
  title: string,
  body: string,
  messageCreatedAt?: string,
) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  if (messageCreatedAt && !shouldAlert(conversationId, messageCreatedAt)) return;

  try {
    const n = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: `chat-${conversationId}`,
    });
    n.onclick = () => {
      try {
        window.focus();
      } catch {
        /* noop */
      }
      dismissChatNotifications(conversationId);
    };
    const list = openNotifications.get(conversationId) || [];
    list.push(n);
    openNotifications.set(conversationId, list);
  } catch {
    /* noop */
  }
}
